import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useHeader } from '@/contexts/HeaderContext';
import Loading from '../components/Loading';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Metrics = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user: contextUser, isAdmin } = useUser();
  const { setHeaderProps } = useHeader();

  // redirect non-admins
  useEffect(() => {
    if (isAdmin === false) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  const [metrics, setMetrics] = useState(null);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);

  const userId = contextUser?.user_id || contextUser?.id;

  useEffect(() => {
    setHeaderProps({ title: t('metrics.title') });
  }, [t, setHeaderProps]);

  const fetchMetrics = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const fetchOptions = token
        ? { headers: { 'Authorization': `Bearer ${token}` } }
        : { credentials: 'include' };

      const response = await fetch(`${API}/metrics/${userId}?period=${period}`, fetchOptions);
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, period]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Period label
  const periodLabel = (p) => {
    switch (p) {
      case 'week': return t('metrics.week');
      case 'month': return t('metrics.month');
      case 'all': return t('metrics.all');
      default: return p;
    }
  };

  // Trend arrow for completion rate
  const ComparisonBadge = ({ current, previous }) => {
    if (previous === null || previous === undefined) return null;
    const diff = current - previous;
    if (diff > 0) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
          <TrendingUp style={{ width: 12, height: 12 }} /> +{diff}%
        </span>
      );
    } else if (diff < 0) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, color: '#dc2626', fontWeight: 600 }}>
          <TrendingDown style={{ width: 12, height: 12 }} /> {diff}%
        </span>
      );
    }
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, color: '#9a9a9a', fontWeight: 600 }}>
        <Minus style={{ width: 12, height: 12 }} /> 0%
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center page-container">
        <Loading size="hundred" message={null} />
      </div>
    );
  }

  return (
    <div style={{ background: 'inherit', display: 'flex', justifyContent: 'center', overflowX: 'hidden' }}>
      <div style={{ width: '100%', maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto', minHeight: 844, background: 'inherit', position: 'relative', paddingBottom: 100, overflowX: 'hidden', overflowY: 'auto' }}>

        {/* Back button */}
        <div style={{ padding: '24px 24px 0' }}>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, stroke: '#0a0a0a', fill: 'none', strokeWidth: 1.5 }}><polyline points="15 18 9 12 15 6" /></svg>
            <span style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 18, color: '#0a0a0a' }}>{i18n.language?.startsWith('it') ? 'Indietro' : i18n.language?.startsWith('es') ? 'Atrás' : 'Back'}</span>
          </button>
        </div>

        <div style={{ height: 1, background: '#e0ddd6', margin: '12px 24px' }} />

        <div style={{ padding: '0 24px' }}>

          {/* Period Selector */}
          <div style={{ display: 'flex', gap: 8, paddingTop: 20, marginBottom: 24 }}>
            {['week', 'month', 'all'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 8,
                  border: period === p ? '1px solid var(--c-texPri)' : '1px solid #e0ddd6',
                  background: period === p ? 'var(--c-texPri)' : 'var(--c-surface)',
                  color: period === p ? 'var(--c-surface)' : 'var(--c-texPri)',
                  fontFamily: 'Manrope-Variable, serif',
                  fontSize: 13,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {periodLabel(p)}
              </button>
            ))}
          </div>

          {metrics && (
            <>
              {/* Engagement Score - Hero Card */}
              <div style={{
                border: '1px solid #e0ddd6', borderRadius: 16, padding: '24px 20px',
                marginBottom: 16, textAlign: 'center', background: 'hsl(var(--background))'
              }}>
                <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', fontSize: 8, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: 8 }}>
                  {t('metrics.engagementScore')}
                </div>
                <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 48, fontWeight: 700, color: '#0a0a0a', lineHeight: 1 }}>
                  {metrics.engagement_score}
                </div>
                <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: '#9a9a9a', marginTop: 4 }}>/ 100</div>
              </div>

              {/* Stat Cards - 2x2 Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {/* Completion Rate */}
                <div style={{ border: '1px solid #e0ddd6', borderRadius: 12, padding: '16px 16px' }}>
                  <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: 4 }}>
                    {t('metrics.completionRate')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 28, fontWeight: 600, color: '#0a0a0a' }}>
                      {metrics.completion_rate}%
                    </div>
                    <ComparisonBadge current={metrics.completion_rate} previous={metrics.prev_completion_rate} />
                  </div>
                </div>

                {/* Current Streak */}
                <div style={{ border: '1px solid #e0ddd6', borderRadius: 12, padding: '16px 16px' }}>
                  <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: 4 }}>
                    {t('metrics.currentStreak')}
                  </div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 28, fontWeight: 600, color: '#0a0a0a' }}>
                    {metrics.current_streak} <span style={{ fontSize: 16 }}>🔥</span>
                  </div>
                  <div style={{ fontSize: 10, color: '#9a9a9a', fontFamily: 'ui-monospace, monospace' }}>
                    {t('metrics.best')}: {metrics.best_streak}
                  </div>
                </div>

                {/* Actions Completed */}
                <div style={{ border: '1px solid #e0ddd6', borderRadius: 12, padding: '16px 16px' }}>
                  <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: 4 }}>
                    {t('metrics.actionsDone')}
                  </div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 28, fontWeight: 600, color: '#0a0a0a' }}>
                    {metrics.actions_completed}
                  </div>
                  <div style={{ fontSize: 10, color: '#9a9a9a', fontFamily: 'ui-monospace, monospace' }}>
                    / {metrics.total_actions} {t('metrics.assigned')}
                  </div>
                </div>

                {/* Time Invested */}
                <div style={{ border: '1px solid #e0ddd6', borderRadius: 12, padding: '16px 16px' }}>
                  <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: 4 }}>
                    {t('metrics.timeInvested')}
                  </div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 28, fontWeight: 600, color: '#0a0a0a' }}>
                    {metrics.total_minutes >= 60
                      ? `${Math.floor(metrics.total_minutes / 60)}h`
                      : `${metrics.total_minutes}m`}
                  </div>
                  {metrics.total_minutes >= 60 && (
                    <div style={{ fontSize: 10, color: '#9a9a9a', fontFamily: 'ui-monospace, monospace' }}>
                      {metrics.total_minutes} min
                    </div>
                  )}
                </div>
              </div>

              {/* Check-ins & Mood */}
              <div style={{ border: '1px solid #e0ddd6', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', fontSize: 8, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: 12 }}>
                  {t('metrics.checkinsAndMood')}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 14, color: '#0a0a0a' }}>
                      {t('metrics.totalCheckins')}
                    </div>
                    <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 22, fontWeight: 600, color: '#0a0a0a' }}>
                      {metrics.checkins_count}
                    </div>
                  </div>
                  {metrics.avg_mood !== null && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 14, color: '#0a0a0a' }}>
                        {t('metrics.avgMood')}
                      </div>
                      <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 22, fontWeight: 600, color: '#0a0a0a' }}>
                        {metrics.avg_mood}/5
                      </div>
                    </div>
                  )}
                </div>

                {/* Mood trend bars */}
                {metrics.mood_trend && metrics.mood_trend.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: 8 }}>
                      {t('metrics.moodTrend')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 60 }}>
                      {metrics.mood_trend.slice(-14).map((m, i) => (
                        <div
                          key={i}
                          style={{
                            flex: 1,
                            height: `${(m.rating / 5) * 100}%`,
                            background: m.rating >= 4 ? '#22c55e' : m.rating >= 3 ? '#f59e0b' : '#ef4444',
                            borderRadius: 3,
                            minHeight: 3,
                            transition: 'height 0.3s ease',
                          }}
                          title={`${m.date}: ${m.rating}/5`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Category Breakdown */}
              <div style={{ border: '1px solid #e0ddd6', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', fontSize: 8, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: 12 }}>
                  {t('metrics.byCategory')}
                </div>
                {Object.entries(metrics.category_breakdown).length > 0 ? (
                  Object.entries(metrics.category_breakdown)
                    .sort(([, a], [, b]) => b - a)
                    .map(([cat, count]) => {
                      const maxCount = Math.max(...Object.values(metrics.category_breakdown));
                      const pct = Math.round((count / maxCount) * 100);
                      return (
                        <div key={cat} style={{ marginBottom: 10 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 13, color: '#0a0a0a', textTransform: 'capitalize' }}>
                              {t(`categories.${cat}`, cat)}
                            </span>
                            <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#6b7280' }}>{count}</span>
                          </div>
                          <div style={{ height: 6, borderRadius: 3, background: '#f0ede6', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: '#0a0a0a', borderRadius: 3, transition: 'width 0.4s ease' }} />
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div style={{ fontSize: 13, color: '#9a9a9a', textAlign: 'center', padding: 20 }}>
                    {t('metrics.noData')}
                  </div>
                )}
              </div>

              {/* Weekly Breakdown */}
              {metrics.weekly_breakdown && metrics.weekly_breakdown.length > 0 && (
                <div style={{ border: '1px solid #e0ddd6', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
                  <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', fontSize: 8, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: 12 }}>
                    {t('metrics.weeklyBreakdown')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 80 }}>
                    {metrics.weekly_breakdown.map((w, i) => {
                      const barHeight = w.rate > 0 ? Math.max(w.rate, 8) : 4;
                      const weekLabel = new Date(w.week_start).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
                      return (
                        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <div style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace', color: '#6b7280' }}>{w.rate}%</div>
                          <div style={{
                            width: '100%', height: `${barHeight}%`, background: w.rate >= 70 ? '#22c55e' : w.rate >= 40 ? '#f59e0b' : '#e0ddd6',
                            borderRadius: 4, minHeight: 4, transition: 'height 0.3s ease',
                          }} />
                          <div style={{ fontSize: 9, fontFamily: 'ui-monospace, monospace', color: '#9a9a9a', textAlign: 'center' }}>{weekLabel}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Skipped info */}
              {metrics.actions_skipped > 0 && (
                <div style={{ border: '1px solid #e0ddd6', borderRadius: 12, padding: '16px 20px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 14, color: '#0a0a0a' }}>
                      {t('metrics.skippedActions')}
                    </div>
                    <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: '#9a9a9a', marginTop: 2 }}>
                      {t('metrics.skippedDesc')}
                    </div>
                  </div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 22, fontWeight: 600, color: '#9a9a9a' }}>
                    {metrics.actions_skipped}
                  </div>
                </div>
              )}
            </>
          )}

          {!metrics && !loading && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9a9a9a' }}>
              <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16 }}>
                {t('metrics.noData')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Metrics;
